/* hooks — useBreakpoint, useDrawerStack, useDebouncedSearchParam, useFakeQuery.
   ХЕНДОФФ: переносятся в frontend/src/hooks/ как отдельные файлы 1:1. */

import React from 'react';
import { useUnsaved } from '../app/providers/UnsavedChangesProvider.jsx';
import { RR } from '../lib/router.jsx';
import {
  createStackNavigator, parseStack, serializeStack, stackParams,
} from './drawerStack.js';
import {
  INITIAL_QUERY_STATE, fetchFail, fetchOk, primeQuery, sameDeps, startFetch, takePrimed,
} from './queryState.js';

  const { useState, useEffect, useCallback, useRef, useMemo } = React;
  const { useSearchParams } = RR;

  // Брейкпоинты: xs<720, sm 720–1199, md 1200–1919, lg 1920–2559, xl≥2560.
  // Порог постоянного сайдбара = 1200: ноутбуки (1280/1366/1440/1536) показывают
  // сайдбар; планшеты/узкие окна <1200 — выезжающий drawer по бургеру.
  function computeBp(w) {
    if (w < 720) return 'xs';
    if (w < 1200) return 'sm';
    if (w < 1920) return 'md';
    if (w < 2560) return 'lg';
    return 'xl';
  }

  // Ширина для брейкпоинта. innerWidth включает зону скроллбара и всегда равен
  // присвоенной ширине iframe — поэтому граничные размерности лаунчера
  // (720/1680/1920/2560) попадают ровно в свой bp. clientWidth — запасной путь.
  function readWidth() {
    if (typeof window !== 'undefined' && window.innerWidth) return window.innerWidth;
    if (typeof document !== 'undefined') return document.documentElement.clientWidth || 1920;
    return 1920;
  }

  function useBreakpoint() {
    const [state, setState] = useState(() => {
      const w = readWidth();
      return { width: w, bp: computeBp(w) };
    });
    useEffect(() => {
      // пересчёт ширины+bp; сравниваем И width, И bp (иначе залипший bp не чинится)
      const apply = () => {
        const w = readWidth();
        setState((prev) => {
          const bp = computeBp(w);
          return (prev.width === w && prev.bp === bp) ? prev : { width: w, bp };
        });
      };
      // немедленный замер после монтирования — лечит протухший innerWidth в iframe
      apply();
      // ВАЖНО: debounce через setTimeout, НЕ requestAnimationFrame. В встроенном/
      // масштабированном iframe (лаунчер) rAF может не вызываться вовсе — тогда bp
      // залипал бы на стартовом значении. setTimeout срабатывает надёжно.
      let timer = null;
      const onResize = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(apply, 80);
      };
      window.addEventListener('resize', onResize);
      // ResizeObserver надёжно ловит смену размера контейнера в iframe (когда
      // событие resize окна не приходит — частый случай при встраивании в лаунчер).
      let ro = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(onResize);
        ro.observe(document.documentElement);
      }
      return () => {
        window.removeEventListener('resize', onResize);
        if (ro) ro.disconnect();
        if (timer) clearTimeout(timer);
      };
    }, []);
    const { bp, width } = state;
    return {
      bp, width,
      isMobile: bp === 'xs' || bp === 'sm',
      isTablet: false,
      isDesktop: bp === 'md' || bp === 'lg',
      isWide: bp === 'xl',
    };
  }

  // useDrawerStack — массив [{type,id}] в URL-параметре ?edit=type-id,…
  // Модель стека — hooks/drawerStack.js (чистая, с тестами). Здесь — живой адрес и сторож:
  // ВСЕ переходы (push / pop / popTo / replaceTop / clear) идут через unsaved.guard,
  // когда меняется верхний редактор (kitchen-ux A2, K-01): грязная форма спросит
  // «Сохранить / Отбросить / Отмена», и адрес не тронут, пока ответа нет. Операции
  // возвращают Promise<boolean> — состоялся ли переход. Стек в операциях читается из
  // ЖИВОГО адреса: после сохранения в диалоге сторожа (new → id) переход ложится поверх.
  // replaceTop пишет адрес replace: «назад» не должно возвращать пустую форму «new»; вкладка
  // редактора (?etab) у той же сущности под настоящим id сохраняется.
  function readLiveStack(fallback) {
    if (typeof window !== 'undefined' && window.location && typeof window.location.search === 'string') {
      return parseStack(new URLSearchParams(window.location.search).get('edit'));
    }
    return fallback;
  }
  function useDrawerStack() {
    const [params, setParams] = useSearchParams();
    const stack = parseStack(params.get('edit'));
    const stackRef = useRef(stack);
    stackRef.current = stack;
    const unsaved = useUnsaved ? useUnsaved() : null;
    const guardRef = useRef(null);
    guardRef.current = unsaved && typeof unsaved.guard === 'function' ? unsaved.guard : null;
    const nav = useMemo(() => createStackNavigator({
      readStack: () => readLiveStack(stackRef.current),
      writeStack: (compute, opts) => setParams(
        (prev) => stackParams(prev, compute(parseStack(prev.get('edit'))), { keepTopScoped: !!(opts && opts.keepTopScoped) }),
        opts && opts.replace ? { replace: true } : undefined,
      ),
      guard: (commit, opts) => {
        const g = guardRef.current;
        if (!g) { commit(); return true; }
        return g(commit, opts);
      },
    }), [setParams]);
    return {
      stack,
      push: nav.push,
      pop: nav.pop,
      clear: nav.clear,
      popAll: nav.clear,                                         // алиас clear (S2 contract)
      popTo: nav.popTo,                                          // закрыть всех потомков уровня index
      replaceTop: nav.replaceTop,
    };
  }

  // useDebouncedSearchParam(name, delay) → [value, setValue]
  function useDebouncedSearchParam(name, delay = 300) {
    const [params, setParams] = useSearchParams();
    const urlVal = params.get(name) || '';
    const [local, setLocal] = useState(urlVal);
    const timer = useRef(null);

    // синхронизация при внешнем изменении URL (назад/вперёд)
    useEffect(() => { setLocal(urlVal); /* eslint-disable-next-line */ }, [urlVal]);

    const setValue = useCallback((v) => {
      setLocal(v);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        setParams((prev) => {
          const sp = new URLSearchParams(prev);
          if (v) sp.set(name, v); else sp.delete(name);
          return sp;
        }, { replace: true });
      }, delay);
    }, [name, delay, setParams]);

    useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
    return [local, setValue];
  }

  // ── возврат во вкладку → мягкий refetch (ST-15) ────────────────────────────
  // Один возврат во вкладку = ОДИН запрос. Возврат приезжает РАЗНЫМИ событиями:
  // alt-tab даёт visibilitychange, клик по окну из другого приложения — только
  // focus, Chrome при переключении вкладок шлёт оба подряд. Поэтому слушаем оба,
  // а дребезг гасим общим окном подавления.
  const REFOCUS_GAP_MS = 800;

  // Фабрика обработчика. Ни document, ни window внутри — вся логика решения
  // «звать ли refetch» проверяется юнит-тестом без браузера.
  function makeRefocusHandler({ isVisible, canRefetch, onRefetch, now = Date.now, gapMs = REFOCUS_GAP_MS }) {
    let lastAt = -Infinity;
    return () => {
      // visibilitychange приходит и на УХОД из вкладки — там перечитывать нечего.
      if (isVisible && !isVisible()) return false;
      // Сторож вызывающего: несохранённый ввод или незавершённая отправка.
      // Проверяем ДО окна подавления — заблокированное событие не должно съедать
      // окно, иначе первый же возврат после «чисто» молча пропадал бы.
      if (canRefetch && !canRefetch()) return false;
      const t = now();
      if (t - lastAt < gapMs) return false;
      lastAt = t;
      onRefetch();
      return true;
    };
  }

  // Подписка целиком — ровно та, которой пользуется useFakeQuery (второго пути
  // нет). Цели событий приходят аргументами, поэтому тест подставляет фейки и
  // проверяет И вызовы, И снятие подписки.
  function subscribeRefocus({ doc, win, ...handlerOpts }) {
    const handle = makeRefocusHandler(handlerOpts);
    if (doc) doc.addEventListener('visibilitychange', handle);
    if (win) win.addEventListener('focus', handle);
    return () => {
      if (doc) doc.removeEventListener('visibilitychange', handle);
      if (win) win.removeEventListener('focus', handle);
    };
  }

  // useFakeQuery(key, fetcher, deps, opts) → {data, loading, refreshing, error, reload, mutate}
  // opts.refetchOnFocus — перечитывать по возврату во вкладку (ST-15). Опция
  // ТОЧКИ ВЫЗОВА, а не поведение по умолчанию: большинству запросов приложения
  // это не нужно, а часть экранов держит несохранённый ввод пользователя.
  // opts.canRefetch — сторож вызывающего: «сейчас перечитывать безопасно?».
  // Stale-while-revalidate (kitchen-ux A2, K-33; переходы — hooks/queryState.js):
  // перезапрос ТОГО ЖЕ запроса (reload, возврат во вкладку) оставляет data и отдаёт
  // refreshing:true — QueryBoundary и редакторы не прячут экран за скелетоном. loading
  // по-прежнему «запрос в полёте» (отправка в POS не шлёт план, пока он пересчитывается).
  // mutate(data) — положить свежие данные без запроса (ответ «Сохранить»).
  // primeQuery(key, data) — данные для следующего монтирования запроса с этим ключом.
  function useFakeQuery(key, fetcher, deps = [], opts = null) {
    const primedRef = useRef(undefined);
    const [state, setState] = useState(() => {
      const primed = takePrimed(key);
      primedRef.current = primed;
      return primed !== undefined
        ? { data: primed, loading: false, refreshing: false, error: null }
        : INITIAL_QUERY_STATE;
    });
    const [nonce, setNonce] = useState(0);
    const identityRef = useRef(null);
    // Поколение запроса: ответ применяется, только если после него не было ни нового
    // запроса, ни mutate — иначе медленный старый ответ (начатый до «Сохранить») затёр
    // бы свежие данные формы и её исходный снимок.
    const genRef = useRef(0);
    useEffect(() => {
      const identity = [key, ...deps];
      const sameIdentity = sameDeps(identityRef.current, identity);
      identityRef.current = identity;
      // данные уже пришли ответом сохранения — первый запрос не нужен
      if (!sameIdentity && primedRef.current !== undefined) { primedRef.current = undefined; return undefined; }
      primedRef.current = undefined;
      let alive = true;
      const gen = ++genRef.current;
      const current = () => alive && gen === genRef.current;
      setState((s) => startFetch(s, { sameIdentity }));
      Promise.resolve()
        .then(() => fetcher())
        .then((data) => { if (current()) setState((s) => fetchOk(s, data)); })
        .catch((err) => {
          // Доменный `code` и серверный `detail` обязаны доезжать до экрана.
          // Пока здесь оставался только {status, message}, экран урока «Академии»
          // видел голый 403 и печатал ЕДИНСТВЕННУЮ известную ему причину —
          // «Доступ заморожен». Ученику с последовательным локом называли чужую
          // причину и обещали продление срока, которого не будет. Реестр причин
          // (lib/academy/errorCodes.js) при этом существовал и на read-пути не
          // использовался, потому что различать было нечем.
          if (current()) {
            setState((s) => fetchFail(s, { status: err.status || 500, message: err.message, code: err.code, detail: err.detail }));
          }
        });
      return () => { alive = false; };
      // eslint-disable-next-line
    }, [key, nonce, ...deps]);
    // reload СТАБИЛЕН по ссылке: он ходит в deps эффектов у вызывающих (например,
    // подписка на focus в Аналитике), и новая функция на каждый рендер заставляла бы
    // их переподписываться бесконечно.
    const reload = useCallback(() => setNonce((n) => n + 1), []);
    // mutate — свежие данные без запроса; запрос, начатый раньше, их уже не перетрёт
    const mutate = useCallback((data) => { genRef.current += 1; setState((s) => fetchOk(s, data)); }, []);

    // ST-15. Сторож держим в ref, а не в deps эффекта: вызывающий пересоздаёт его
    // на каждом рендере, а подписка обязана быть ОДНА на жизнь хука (иначе
    // слушатели переподписывались бы пачкой на каждый рендер). refetchOnFocus и
    // reload стабильны, поэтому эффект отрабатывает один раз и снимается на
    // размонтировании.
    const refetchOnFocus = !!(opts && opts.refetchOnFocus);
    const canRefetchRef = useRef(null);
    canRefetchRef.current = (opts && opts.canRefetch) || null;
    useEffect(() => {
      if (!refetchOnFocus) return undefined;
      if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;
      return subscribeRefocus({
        doc: document,
        win: window,
        isVisible: () => document.visibilityState !== 'hidden',
        canRefetch: () => {
          const guard = canRefetchRef.current;
          return guard ? !!guard() : true;
        },
        onRefetch: reload,
      });
    }, [refetchOnFocus, reload]);

    return { ...state, reload, mutate };
  }

  export { makeRefocusHandler, subscribeRefocus, REFOCUS_GAP_MS };
  export const hooks = {
    useBreakpoint, useDrawerStack, useDebouncedSearchParam, useFakeQuery, parseStack, serializeStack, primeQuery,
  };
